export const MANAGE_TICKET_COLUMN_IDS = [
  'event',
  'section',
  'row',
  'seats',
  'status',
  'marketplaces',
  'vlShare',
  'price',
  'cost',
  'potentialPayout',
  'difference',
  'lastPriced',
  'clientName',
  'returnEmail',
  'clientType',
] as const;

export type ManageTicketColumnId = (typeof MANAGE_TICKET_COLUMN_IDS)[number];

const COLUMN_ID_SET = new Set<string>(MANAGE_TICKET_COLUMN_IDS);

export const isManageTicketColumnId = (value: string): value is ManageTicketColumnId =>
  COLUMN_ID_SET.has(value);

const storageKey = (userId: string, tab: string) =>
  `manageTicketsColumnOrder:${userId || 'anonymous'}:${tab}`;

export const readManageTicketColumnOrder = (
  userId: string,
  tab: string,
): ManageTicketColumnId[] | null => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey(userId, tab));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const ids = parsed.filter((id): id is ManageTicketColumnId =>
      typeof id === 'string' && isManageTicketColumnId(id),
    );
    return ids.length ? ids : null;
  } catch {
    return null;
  }
};

export const writeManageTicketColumnOrder = (
  userId: string,
  tab: string,
  order: readonly ManageTicketColumnId[],
) => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(storageKey(userId, tab), JSON.stringify(order));
};

/** Visible columns in the saved order. Columns this tab does not show are skipped. */
export const visibleManageTicketColumnOrder = (
  saved: readonly string[] | null | undefined,
  visible: readonly ManageTicketColumnId[],
): ManageTicketColumnId[] => {
  const visibleSet = new Set(visible);
  const placed = new Set<ManageTicketColumnId>();
  const result: ManageTicketColumnId[] = [];
  for (const id of saved ?? []) {
    if (!isManageTicketColumnId(id) || !visibleSet.has(id) || placed.has(id)) continue;
    result.push(id);
    placed.add(id);
  }
  for (const id of MANAGE_TICKET_COLUMN_IDS) {
    if (!visibleSet.has(id) || placed.has(id)) continue;
    const defaultIndex = MANAGE_TICKET_COLUMN_IDS.indexOf(id);
    let insertAt = result.length;
    for (let index = 0; index < result.length; index += 1) {
      if (MANAGE_TICKET_COLUMN_IDS.indexOf(result[index]) > defaultIndex) {
        insertAt = index;
        break;
      }
    }
    result.splice(insertAt, 0, id);
    placed.add(id);
  }
  return result;
};

/**
 * Move a visible column and keep hidden columns in their saved slots
 * so they reappear in the same place on a tab that shows them.
 */
export const reorderVisibleManageTicketColumns = (
  previousFull: readonly ManageTicketColumnId[] | null | undefined,
  visible: readonly ManageTicketColumnId[],
  fromId: ManageTicketColumnId,
  toId: ManageTicketColumnId,
): ManageTicketColumnId[] => {
  const fromIndex = visible.indexOf(fromId);
  const toIndex = visible.indexOf(toId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return [...(previousFull ?? MANAGE_TICKET_COLUMN_IDS)];
  }
  const nextVisible = [...visible];
  const [moved] = nextVisible.splice(fromIndex, 1);
  nextVisible.splice(toIndex, 0, moved);

  const visibleSet = new Set(visible);
  const base = previousFull?.length ? previousFull : MANAGE_TICKET_COLUMN_IDS;
  const queue = [...nextVisible];
  const merged: ManageTicketColumnId[] = [];
  const seen = new Set<ManageTicketColumnId>();
  for (const id of base) {
    if (!isManageTicketColumnId(id) || seen.has(id)) continue;
    if (visibleSet.has(id)) {
      const next = queue.shift();
      if (next && !seen.has(next)) {
        merged.push(next);
        seen.add(next);
      }
    } else {
      merged.push(id);
      seen.add(id);
    }
  }
  for (const id of queue) {
    if (!seen.has(id)) {
      merged.push(id);
      seen.add(id);
    }
  }
  for (const id of MANAGE_TICKET_COLUMN_IDS) {
    if (!seen.has(id)) merged.push(id);
  }
  return merged;
};
